import React, { useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Grid, Stack, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, LinearProgress, Alert, Chip, Divider, Tabs, Tab } from '@mui/material';
import { RestartAlt, ShowChart, TrendingUp, CompareArrows, CalendarMonth, Person, Dashboard as DashboardIcon, EmojiEvents, GroupAdd } from '@mui/icons-material';
import { BarChart, Bar, AreaChart, Area, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, Legend, ResponsiveContainer } from 'recharts';
import api from '../services/api';
import { ProductSeasonalityReport, ProductSeasonalitySummary, SalesForecastResponse, YoYComparisonResponse, QuarterlyBreakdownResponse, SalesByCustomerTypeResponse, ForecastDashboard } from '../types';
import StrategicClientReport from '../components/reports/StrategicClientReport';
import TopProductsReport from '../components/reports/TopProductsReport';
import NewVsReturningReport from '../components/reports/NewVsReturningReport';
import TextField from '../components/common/TextField';

const formatMonthValue = (date: Date) => `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
const monthToIso = (value?: string | null, endOfMonth = false) => {
  if (!value) return undefined;
  const [yStr, mStr] = value.split('-');
  const y = Number(yStr);
  const m = Number(mStr);
  if (!y || !m) return undefined;
  const day = endOfMonth ? new Date(Date.UTC(y, m, 0)).getUTCDate() : 1;
  return new Date(Date.UTC(y, m - 1, day)).toISOString();
};

export const ReportsPage: React.FC = () => {
  const today = useMemo(() => new Date(), []);
  const [activeTab, setActiveTab] = useState(0);
  const [featureFlags, setFeatureFlags] = useState<Set<string>>(new Set());
  const [flagsReady, setFlagsReady] = useState(false);
  const [seasonality, setSeasonality] = useState<ProductSeasonalityReport | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [fromMonth, setFromMonth] = useState(formatMonthValue(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 11, 1))));
  const [toMonth, setToMonth] = useState(formatMonthValue(today));
  const [top, setTop] = useState(15);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Forecast data
  const [forecast, setForecast] = useState<SalesForecastResponse | null>(null);
  const [forecastLoading, setForecastLoading] = useState(false);

  // YoY comparison data
  const [yoy, setYoy] = useState<YoYComparisonResponse | null>(null);
  const [yoyLoading, setYoyLoading] = useState(false);

  // Quarterly data
  const [quarterly, setQuarterly] = useState<QuarterlyBreakdownResponse | null>(null);
  const [quarterlyLoading, setQuarterlyLoading] = useState(false);

  // Customer type data
  const [customerType, setCustomerType] = useState<SalesByCustomerTypeResponse | null>(null);
  const [customerTypeLoading, setCustomerTypeLoading] = useState(false);

  // CRM Dashboard data
  const [forecastDash, setForecastDash] = useState<ForecastDashboard | null>(null);
  const [monthlyOps, setMonthlyOps] = useState<{ month: string; count: number }[]>([]);
  const [monthlyBilling, setMonthlyBilling] = useState<{ month: string; total: number }[]>([]);
  const [quarterlyData, setQuarterlyData] = useState<{ quarter: string; actual: number; estimated: number }[]>([]);
  const [salesByType, setSalesByType] = useState<{ month: string; type: string; total: number }[]>([]);
  const [customerPieQ, setCustomerPieQ] = useState<{ customer: string; total: number }[]>([]);
  const [customerPieY, setCustomerPieY] = useState<{ customer: string; total: number }[]>([]);
  const [crmLoading, setCrmLoading] = useState(false);

  const PIE_COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#d0ed57', '#a4de6c', '#8dd1e1'];

  const loadCrmDashboard = async () => {
    setCrmLoading(true);
    try {
      const [fd, ops, billing, qd, sbt, cpq, cpy] = await Promise.all([
        api.getForecastDashboard(),
        api.getMonthlyOperationsChart(12),
        api.getMonthlyBillingChart(12),
        api.getQuarterlyChart(8),
        api.getSalesByTypeChart(12),
        api.getCustomerPieQuarter(),
        api.getCustomerPieYear(),
      ]);
      setForecastDash(fd);
      setMonthlyOps(ops.items || []);
      setMonthlyBilling(billing.items || []);
      setQuarterlyData(qd.items || []);
      setSalesByType(sbt.items || []);
      setCustomerPieQ(cpq.items || []);
      setCustomerPieY(cpy.items || []);
    } catch (e) {
      console.error('Error loading CRM dashboard', e);
    } finally {
      setCrmLoading(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const flags = await api.getFeatureFlags();
        setFeatureFlags(new Set((flags || []).filter(f => f.enabled).map(f => f.key.toLowerCase())));
      } catch {
        setFeatureFlags(new Set());
      }
      setFlagsReady(true);
    })();

    // Load reports data
    loadForecast();
    loadYoY();
    loadQuarterly();
    loadCustomerType();
    loadCrmDashboard();
  }, []);

  const loadForecast = async () => {
    setForecastLoading(true);
    try {
      const data = await api.getSalesForecast(6);
      setForecast(data);
    } catch (e) {
      console.error('Error loading forecast', e);
    } finally {
      setForecastLoading(false);
    }
  };

  const loadYoY = async () => {
    setYoyLoading(true);
    try {
      const data = await api.getYoYComparison({ monthsBack: 6 });
      setYoy(data);
    } catch (e) {
      console.error('Error loading YoY', e);
    } finally {
      setYoyLoading(false);
    }
  };

  const loadQuarterly = async () => {
    setQuarterlyLoading(true);
    try {
      const data = await api.getQuarterlyBreakdown();
      setQuarterly(data);
    } catch (e) {
      console.error('Error loading quarterly', e);
    } finally {
      setQuarterlyLoading(false);
    }
  };

  const loadCustomerType = async () => {
    setCustomerTypeLoading(true);
    try {
      const data = await api.getSalesByCustomerType();
      setCustomerType(data);
    } catch (e) {
      console.error('Error loading customer type', e);
    } finally {
      setCustomerTypeLoading(false);
    }
  };

  const hasFlag = (key: string) => featureFlags.has(key.toLowerCase());

  const loadSeasonality = async () => {
    if (!hasFlag('product-seasonality')) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getProductSeasonality({
        from: monthToIso(fromMonth, false),
        to: monthToIso(toMonth, true),
        top,
      });
      setSeasonality(data);
      setSelectedProductId(prev => {
        if (data.summary?.some(s => s.productId === prev)) return prev;
        return data.summary?.[0]?.productId ?? null;
      });
    } catch (e: any) {
      setSeasonality(null);
      setSelectedProductId(null);
      setError(e?.response?.data?.message || 'No se pudo obtener el reporte.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!flagsReady) return;
    if (hasFlag('product-seasonality')) {
      loadSeasonality();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flagsReady, featureFlags]);

  const summary = seasonality?.summary || [];

  const detailRows = useMemo(() => {
    if (!seasonality) return [];
    if (!selectedProductId) return seasonality.items || [];
    return (seasonality.items || []).filter(it => it.productId === selectedProductId);
  }, [seasonality, selectedProductId]);

  const selectedSummary: ProductSeasonalitySummary | null = useMemo(() => {
    if (!selectedProductId) return null;
    return summary.find(s => s.productId === selectedProductId) || null;
  }, [summary, selectedProductId]);

  const resetRange = () => {
    setFromMonth(formatMonthValue(new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() - 11, 1))));
    setToMonth(formatMonthValue(today));
    setError(null);
  };

  // Reportes en millones de pesos: todos los importes se muestran como "$X M".
  const formatM = (value: number) =>
    `$${((Number(value) || 0) / 1_000_000).toLocaleString('es-AR', { maximumFractionDigits: 2 })} M`;
  // Etiquetas de ejes: versión corta (1 decimal) para no encimar los números.
  const formatAxisM = (value: number) =>
    `$${((Number(value) || 0) / 1_000_000).toLocaleString('es-AR', { maximumFractionDigits: 1 })} M`;
  const formatCurrency = formatM;
  const formatCurrencyCompact = formatM;

  const getChangeColor = (change: number) => {
    if (change > 0) return 'success.main';
    if (change < 0) return 'error.main';
    return 'text.secondary';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>Reportes</Typography>

      <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }} variant="scrollable" scrollButtons="auto">
        <Tab icon={<DashboardIcon />} iconPosition="start" label="Dashboard CRM" />
        <Tab icon={<TrendingUp />} iconPosition="start" label="Forecast" />
        <Tab icon={<CompareArrows />} iconPosition="start" label="Comparativo YoY" />
        <Tab icon={<CalendarMonth />} iconPosition="start" label="Por Trimestre" />
        <Tab icon={<Person />} iconPosition="start" label="Clientes Estratégicos" />
        <Tab icon={<GroupAdd />} iconPosition="start" label="Nuevos vs Recurrentes" />
        <Tab icon={<EmojiEvents />} iconPosition="start" label="Top Vendidos" />
        <Tab icon={<ShowChart />} iconPosition="start" label="Estacionalidad" />
      </Tabs>

      {/* Tab 0: CRM Dashboard */}
      {activeTab === 0 && (
        <Box>
          {crmLoading && <LinearProgress sx={{ mb: 2 }} />}

          {/* 6 KPI Numbers */}
          {forecastDash && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={2}>
                <Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Forecast {forecastDash.month}</Typography>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(forecastDash.forecastTotal)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card sx={{ bgcolor: '#E8F5E9' }}><CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Up to Date {forecastDash.month}</Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">{formatCurrency(forecastDash.upToDateTotal)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Forecast Sin Fason</Typography>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(forecastDash.forecastSinFason)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card sx={{ bgcolor: '#E8F5E9' }}><CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Up to Date Sin Fason</Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">{formatCurrency(forecastDash.upToDateSinFason)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card><CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Forecast Fason</Typography>
                  <Typography variant="h6" fontWeight={700}>{formatCurrency(forecastDash.forecastFason)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={2}>
                <Card sx={{ bgcolor: '#E8F5E9' }}><CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Up to Date Fason</Typography>
                  <Typography variant="h6" fontWeight={700} color="success.main">{formatCurrency(forecastDash.upToDateFason)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={6}>
                <Card sx={{ bgcolor: '#E3F2FD' }}><CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Forecast FY</Typography>
                  <Typography variant="h5" fontWeight={700} color="primary">{formatCurrency(forecastDash.forecastFY)}</Typography>
                </CardContent></Card>
              </Grid>
              <Grid item xs={6} md={6}>
                <Card sx={{ bgcolor: '#E8F5E9' }}><CardContent sx={{ textAlign: 'center', py: 1 }}>
                  <Typography variant="caption" color="text.secondary">Up to Date FY</Typography>
                  <Typography variant="h5" fontWeight={700} color="success.main">{formatCurrency(forecastDash.upToDateFY)}</Typography>
                </CardContent></Card>
              </Grid>
            </Grid>
          )}

          {/* Chart 1: Monthly Operations */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Card sx={{ mb: 2 }}><CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Operaciones por Mes</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={monthlyOps}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="count" fill="#2196F3" name="Operaciones" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent></Card>
            </Grid>

            {/* Chart 2: Monthly Billing */}
            <Grid item xs={12} md={6}>
              <Card sx={{ mb: 2 }}><CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Facturado por mes</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={monthlyBilling}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={formatAxisM} />
                    <RechartsTooltip formatter={(v: any) => formatCurrency(Number(v))} />
                    <Bar dataKey="total" fill="#4CAF50" name="Facturado" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent></Card>
            </Grid>

            {/* Chart 3: Quarterly */}
            <Grid item xs={12} md={6}>
              <Card sx={{ mb: 2 }}><CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Facturación Trimestral</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={quarterlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="quarter" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={formatAxisM} />
                    <RechartsTooltip formatter={(v: any) => formatCurrency(Number(v))} />
                    <Bar dataKey="actual" fill="#4CAF50" name="Real" />
                    <Bar dataKey="estimated" fill="#FFC107" name="Estimado" />
                    <Legend />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent></Card>
            </Grid>

            {/* Chart 4: Stacked Bar by Type */}
            <Grid item xs={12} md={6}>
              <Card sx={{ mb: 2 }}><CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Ventas por Tipo (Barras)</Typography>
                <ResponsiveContainer width="100%" height={250}>
                  {(() => {
                    const types = Array.from(new Set(salesByType.map(d => d.type)));
                    const months = Array.from(new Set(salesByType.map(d => d.month))).sort();
                    const chartData = months.map(m => {
                      const row: any = { month: m };
                      types.forEach(t => { row[t] = salesByType.find(d => d.month === m && d.type === t)?.total || 0; });
                      return row;
                    });
                    return (
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tickFormatter={formatAxisM} />
                        <RechartsTooltip formatter={(v: any) => formatCurrency(Number(v))} />
                        <Legend />
                        {types.map((t, i) => <Bar key={t} dataKey={t} stackId="a" fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </BarChart>
                    );
                  })()}
                </ResponsiveContainer>
              </CardContent></Card>
            </Grid>

            {/* Chart 5: Stacked Area by Type */}
            <Grid item xs={12}>
              <Card sx={{ mb: 2 }}><CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Ventas por Tipo (Área)</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  {(() => {
                    const types = Array.from(new Set(salesByType.map(d => d.type)));
                    const months = Array.from(new Set(salesByType.map(d => d.month))).sort();
                    const chartData = months.map(m => {
                      const row: any = { month: m };
                      types.forEach(t => { row[t] = salesByType.find(d => d.month === m && d.type === t)?.total || 0; });
                      return row;
                    });
                    return (
                      <AreaChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                        <YAxis tickFormatter={formatAxisM} />
                        <RechartsTooltip formatter={(v: any) => formatCurrency(Number(v))} />
                        <Legend />
                        {types.map((t, i) => <Area key={t} type="monotone" dataKey={t} stackId="1" stroke={PIE_COLORS[i % PIE_COLORS.length]} fill={PIE_COLORS[i % PIE_COLORS.length]} fillOpacity={0.6} />)}
                      </AreaChart>
                    );
                  })()}
                </ResponsiveContainer>
              </CardContent></Card>
            </Grid>

            {/* Chart 6: Pie Quarter */}
            <Grid item xs={12} md={6}>
              <Card sx={{ mb: 2 }}><CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Clientes - Trimestre en Curso</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={customerPieQ} dataKey="total" nameKey="customer" cx="50%" cy="50%" outerRadius={100} label={({ customer, percent }: any) => `${customer} ${((percent || 0) * 100).toFixed(0)}%`}>
                      {customerPieQ.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <RechartsTooltip formatter={(v: any) => formatCurrency(Number(v))} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent></Card>
            </Grid>

            {/* Chart 7: Pie Year */}
            <Grid item xs={12} md={6}>
              <Card sx={{ mb: 2 }}><CardContent>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Clientes - Año en Curso</Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie data={customerPieY} dataKey="total" nameKey="customer" cx="50%" cy="50%" outerRadius={100} label={({ customer, percent }: any) => `${customer} ${((percent || 0) * 100).toFixed(0)}%`}>
                      {customerPieY.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie>
                    <RechartsTooltip formatter={(v: any) => formatCurrency(Number(v))} />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent></Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Tab 1: Forecast */}
      {activeTab === 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <TrendingUp />
              <Typography variant="h6" fontWeight={600}>Forecast de Ventas</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ventas actuales más proyección basada en oportunidades activas.
            </Typography>
            {forecastLoading && <LinearProgress />}
            {forecast && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {forecast.months.map((m, idx) => (
                    <Grid item xs={12} sm={6} md={4} lg={2} key={m.month}>
                      <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: idx === 0 ? 'primary.light' : undefined }}>
                        <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                        <Typography variant="h5" fontWeight={700}>{formatCurrencyCompact(m.actualSales)}</Typography>
                        <Typography variant="caption" color="text.secondary">Actual</Typography>
                        {m.expectedValue > 0 && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="body2" color="info.main">+{formatCurrencyCompact(m.expectedValue)}</Typography>
                            <Typography variant="caption" color="text.secondary">Proyectado ({m.opportunityCount} opps)</Typography>
                          </>
                        )}
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Stack direction="row" spacing={4}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Mes Actual</Typography>
                    <Typography variant="h6">{formatCurrency(forecast.currentMonthActual)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Proyectado (6 meses)</Typography>
                    <Typography variant="h6" color="info.main">{formatCurrency(forecast.totalProjected)}</Typography>
                  </Box>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 2: YoY Comparison */}
      {activeTab === 2 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CompareArrows />
              <Typography variant="h6" fontWeight={600}>Comparación Año vs Año</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Compara ventas del año actual con el año anterior mes a mes.
            </Typography>
            {yoyLoading && <LinearProgress />}
            {yoy && (
              <>
                <Stack direction="row" spacing={3} sx={{ mb: 3 }}>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{yoy.previousYear}</Typography>
                    <Typography variant="h5">{formatCurrencyCompact(yoy.previousYearTotal)}</Typography>
                  </Card>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{yoy.currentYear}</Typography>
                    <Typography variant="h5">{formatCurrencyCompact(yoy.currentYearTotal)}</Typography>
                  </Card>
                  <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: yoy.overallChangePercentage >= 0 ? 'success.light' : 'error.light' }}>
                    <Typography variant="caption" color="text.secondary">Variación</Typography>
                    <Typography variant="h5" sx={{ color: getChangeColor(yoy.overallChangePercentage) }}>
                      {yoy.overallChangePercentage >= 0 ? '+' : ''}{yoy.overallChangePercentage.toFixed(1)}%
                    </Typography>
                  </Card>
                </Stack>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Mes</TableCell>
                        <TableCell align="right">{yoy.previousYear}</TableCell>
                        <TableCell align="right">{yoy.currentYear}</TableCell>
                        <TableCell align="right">Cambio</TableCell>
                        <TableCell align="right">%</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {yoy.months.map((m) => (
                        <TableRow key={m.month}>
                          <TableCell>{m.monthName}</TableCell>
                          <TableCell align="right">{formatCurrency(m.previousYearSales)}</TableCell>
                          <TableCell align="right">{formatCurrency(m.currentYearSales)}</TableCell>
                          <TableCell align="right" sx={{ color: getChangeColor(m.changeAmount) }}>
                            {m.changeAmount >= 0 ? '+' : ''}{formatCurrency(m.changeAmount)}
                          </TableCell>
                          <TableCell align="right" sx={{ color: getChangeColor(m.changePercentage) }}>
                            {m.changePercentage >= 0 ? '+' : ''}{m.changePercentage.toFixed(1)}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 3: Quarterly Breakdown */}
      {activeTab === 3 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <CalendarMonth />
              <Typography variant="h6" fontWeight={600}>Desglose por Trimestres</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Ventas agrupadas por trimestre para el año {quarterly?.year || new Date().getFullYear()}.
            </Typography>
            {quarterlyLoading && <LinearProgress />}
            {quarterly && (
              <>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  {quarterly.quarters.map((q) => (
                    <Grid item xs={12} sm={6} md={3} key={q.quarter}>
                      <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="h6" fontWeight={700}>{q.label}</Typography>
                        <Typography variant="h4" fontWeight={700} color="primary.main">{formatCurrencyCompact(q.totalSales)}</Typography>
                        <Stack direction="row" justifyContent="center" spacing={2} sx={{ mt: 1 }}>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Órdenes</Typography>
                            <Typography variant="body2">{q.orderCount}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Ticket Prom.</Typography>
                            <Typography variant="body2">{formatCurrency(q.avgOrderValue)}</Typography>
                          </Box>
                          <Box>
                            <Typography variant="caption" color="text.secondary">Unidades</Typography>
                            <Typography variant="body2">{q.unitsSold.toLocaleString()}</Typography>
                          </Box>
                        </Stack>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" spacing={4}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Año {quarterly.year}</Typography>
                    <Typography variant="h5" fontWeight={700}>{formatCurrency(quarterly.yearTotal)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Total Órdenes</Typography>
                    <Typography variant="h5">{quarterly.yearOrderCount.toLocaleString()}</Typography>
                  </Box>
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab 4: Strategic Clients */}
      {activeTab === 4 && (
        <StrategicClientReport />
      )}

      {/* Tab 5: New vs Returning customers */}
      {activeTab === 5 && (
        <NewVsReturningReport />
      )}

      {/* Tab 6: Top Products */}
      {activeTab === 6 && (
        <TopProductsReport />
      )}

      {/* Tab 7: Seasonality (existing) */}
      {activeTab === 7 && (
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <ShowChart fontSize="small" />
              <Typography variant="h6" fontWeight={600}>Estacionalidad por producto</Typography>
            </Stack>
            <Chip label="Beta" size="small" color="default" />
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ventas por producto y mes para detectar picos de demanda, planificar compras y priorizar campañas.
          </Typography>
          {!flagsReady && <LinearProgress />}
          {flagsReady && !hasFlag('product-seasonality') && (
            <Alert severity="info">Activa la bandera product-seasonality para ver este reporte.</Alert>
          )}
          {flagsReady && hasFlag('product-seasonality') && (
            <>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                <TextField
                  label="Desde (mes)"
                  type="month"
                  size="small"
                  value={fromMonth}
                  onChange={e => setFromMonth(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Hasta (mes)"
                  type="month"
                  size="small"
                  value={toMonth}
                  onChange={e => setToMonth(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  label="Top productos"
                  type="number"
                  size="small"
                  value={top}
                  onChange={e => {
                    const v = Number(e.target.value);
                    const next = !Number.isFinite(v) || v <= 0 ? 1 : Math.min(200, Math.round(v));
                    setTop(next);
                  }}
                  inputProps={{ min: 1, max: 200 }}
                  sx={{ width: 140 }}
                />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ width: '100%', maxWidth: 320 }}>
                  <Button variant="outlined" startIcon={<RestartAlt />} onClick={resetRange}>Restablecer</Button>
                  <Button variant="contained" onClick={loadSeasonality} disabled={loading}>Actualizar</Button>
                </Stack>
              </Stack>
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {loading && <LinearProgress sx={{ mb: 2 }} />}
              {!loading && seasonality && (
                <>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mb: 2 }}>
                    <Chip label={`Período: ${new Date(seasonality.from).toLocaleDateString()} - ${new Date(seasonality.to).toLocaleDateString()}`} />
                    <Chip label={`${summary.length} productos`} />
                    <Chip label={`${detailRows.length} filas`} />
                  </Stack>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>Resumen por producto</Typography>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Producto</TableCell>
                              <TableCell>SKU</TableCell>
                              <TableCell align="right">Unidades</TableCell>
                              <TableCell align="right">Ventas</TableCell>
                              <TableCell>Mejor mes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {summary.map(row => (
                              <TableRow
                                key={row.productId}
                                hover
                                selected={selectedProductId === row.productId}
                                onClick={() => setSelectedProductId(row.productId)}
                                sx={{ cursor: 'pointer' }}
                              >
                                <TableCell>{row.productName}</TableCell>
                                <TableCell>{row.sku || '—'}</TableCell>
                                <TableCell align="right">{row.totalUnits.toLocaleString()}</TableCell>
                                <TableCell align="right">{formatCurrency(row.totalSales)}</TableCell>
                                <TableCell>{row.peakMonth ? `${row.peakMonth} (${row.peakUnits.toLocaleString()} u)` : '—'}</TableCell>
                              </TableRow>
                            ))}
                            {summary.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={5}>
                                  <Typography variant="body2" color="text.secondary">Sin datos en el período seleccionado.</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600}>Detalle mensual</Typography>
                        {selectedSummary && (
                          <Chip
                            size="small"
                            label={`Pico: ${selectedSummary.peakMonth || '—'} (${selectedSummary.peakUnits.toLocaleString()} u)`}
                          />
                        )}
                      </Stack>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Mes</TableCell>
                              <TableCell>Producto</TableCell>
                              <TableCell align="right">Unidades</TableCell>
                              <TableCell align="right">Ventas</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detailRows.map(row => (
                              <TableRow key={`${row.productId}-${row.month}`}>
                                <TableCell>{row.month}</TableCell>
                                <TableCell>{row.productName}</TableCell>
                                <TableCell align="right">{row.units.toLocaleString()}</TableCell>
                                <TableCell align="right">{formatCurrency(row.total)}</TableCell>
                              </TableRow>
                            ))}
                            {detailRows.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={4}>
                                  <Typography variant="body2" color="text.secondary">Seleccioná un producto para ver su desglose.</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Grid>
                  </Grid>
                  <Divider sx={{ mt: 3, mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Solo se muestran los productos más vendidos en el período (ajusta el Top para ampliar o enfocar).
                  </Typography>
                </>
              )}
              {!loading && !seasonality && !error && (
                <Typography variant="body2" color="text.secondary">No hay datos de ventas para el rango elegido.</Typography>
              )}
            </>
          )}
        </CardContent>
      </Card>
      )}
    </Box>
  );
};
